import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "appSource" }
const _hoisted_2 = { class: "button-row source" }
const _hoisted_3 = ["options", "value"]
const _hoisted_4 = {
  key: 0,
  class: "spacer source"
}
const _hoisted_5 = { class: "button-row" }
const _hoisted_6 = ["value", "label"]
const _hoisted_7 = {
  key: 1,
  class: "spacer source"
}
const _hoisted_8 = { class: "button-row" }
const _hoisted_9 = ["value", "label"]
const _hoisted_10 = {
  key: 2,
  class: "spacer source"
}
const _hoisted_11 = ["value", "label"]
const _hoisted_12 = {
  key: 0,
  class: "spacer source"
}
const _hoisted_13 = ["value", "options", "disabled", "loading"]
const _hoisted_14 = {
  key: 0,
  class: "error-message"
}
const _hoisted_15 = { class: "spacer source" }
const _hoisted_16 = ["value", "label"]
const _hoisted_17 = {
  key: 0,
  class: "error-message"
}
const _hoisted_18 = { class: "spacer source" }
const _hoisted_19 = ["value", "label", "disabled"]

import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import jsyaml from 'js-yaml';
import GitPicker from './GitPicker.vue';
import { generateZip } from '../../utils/download';
import {
  APPLICATION_SOURCE_TYPE,
} from '../../types';
import { useGitConfigs } from '../../queries/useGitConfigQueries';
import { ResourceQueryOptions, ListResourceRequestParams } from '../../models/resource/ui-types';
import debounce from 'lodash/debounce';
import { AppFormSource, App, AppManifest } from '../../models/application/ui-types';
import { ApiAppManifest } from '../../models/application/api-types';
import { toAppManifest } from '../../models/application/mappers';

interface FileWithRelativePath extends File {
  // For some reason TS throws this as missing at transpile time .. so recreate it
   readonly webkitRelativePath: string;
}

type GitPickerSource = AppFormSource['github'] | AppFormSource['gitlab'];

export default /*@__PURE__*/_defineComponent({
  __name: 'AppSource',
  props: {
    source: {},
    mode: {},
    updateSource: { type: Function },
    populateFormFromApp: { type: Function }
  },
  setup(__props: any) {


const store = useStore();

const t = store.getters['i18n/t'];

const props = __props;

const isEdit = computed(() => props.mode === 'edit');

// Casts live here: a `|` in a template binding parses as a Vue 2 filter.
const gitPickerSource = computed(
  () => props.source[props.source.type] as GitPickerSource
);
const gitPickerType = computed(
  () => props.source.type as 'github' | 'gitlab'
);

const manifestFileInput = ref<HTMLInputElement | null>(null);
const archiveFileInput = ref<HTMLInputElement | null>(null);
const folderFileInput = ref<HTMLInputElement | null>(null);
const fileDialogActive = ref(false);
// Set when the config read is refused, which is a valid role, not a fault.
const gitConfigsForbidden = ref(false);

const gitConfigRequestParams = ref<ListResourceRequestParams>({
  page: 1,
  pageSize: 25,
  search: '',
});
const gitConfigRequestOptions = ref<ResourceQueryOptions>({
  enabled: true,
  polling: false,
});
const {data: gitConfigs, isLoading: isLoadingGitConfigs, isError: isErrorGitConfigs} = useGitConfigs(store, gitConfigRequestParams, gitConfigRequestOptions);
const onGitConfigFilter = debounce((query: string) => {
  gitConfigRequestParams.value.page = 1;
  gitConfigRequestParams.value.search = query;
}, 500);

// Derived and Computed
const types = Object.values(APPLICATION_SOURCE_TYPE).map(value => ({
  label: t(`epinio.applications.steps.source.${ value }.label`),
  value
}));

const validGitUrl = computed(() => {
  if (!props.source.gitUrl?.url) return false;

  const gitRegex = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,})/gm;
  const result = gitRegex.exec(props.source.gitUrl?.url || '');

  return !!result && props.source.gitUrl?.url === result[0];
});

function onFileSelected(file: File) {
  props.updateSource(props.source.type, { tarball: file, name: file.name });
}

function handleArchiveFileClick() {
  if (archiveFileInput.value) {
    fileDialogActive.value = true;
    archiveFileInput.value.click();
  }
}

function handleArchiveFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    onFileSelected(input.files[0]);
    input.value = ''; // Clear the input so the same file can be selected again if needed
  }
}

function handleFromManifestClick() {
  if (manifestFileInput.value) {
    fileDialogActive.value = true;
    manifestFileInput.value.click();
  }
}

async function handleManifestFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    const file = input.files[0];
    const content = await file.text();
    onManifestFileSelected(content);
    input.value = ''; // Clear the input so the same file can be selected again if needed
  }
}

function onManifestFileSelected(file: string) {
  try {
    const parsed: ApiAppManifest = jsyaml.load(file);
    const manifest: AppManifest = toAppManifest(parsed);
    if (!parsed) throw new Error('Parsed manifest is empty');
    props.populateFormFromApp(manifest, false);
  } catch (e) {
    console.error('Failed to parse manifest:', e);
  }
}

function handleFolderFileClick() {
  if (folderFileInput.value) {
    fileDialogActive.value = true;
    folderFileInput.value.click();
  }
}

function handleFolderFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files.length > 0) {
    onFolderSelected(Array.from(input.files) as FileWithRelativePath[]);
    input.value = ''; // Clear the input so the same folder can be selected again if needed
  }
}

function onFolderSelected(files: FileWithRelativePath | FileWithRelativePath[]) {
  const safeFiles = Array.isArray(files) ? files : [files];
  let folderName = '';

  for (const f of safeFiles) {
    const paths = f.webkitRelativePath.split('/');
    if (paths.length > 1) {
      if (!folderName) {
        folderName = paths[0];
      } else if (folderName !== paths[0]) {
        folderName = '';
        break;
      }
    }
  }

  const filesToZip = safeFiles.reduce((res, f) => {
    let path = f.webkitRelativePath;
    if (folderName) {
      const parts = path.split('/');
      parts.shift();
      path = parts.join('/');
    }
    res[path] = f;
    return res;
  }, {} as { [key: string]: any });

  generateZip(filesToZip).then((zip: any) => {
    props.updateSource(props.source.type, { tarball: zip, name: folderName || 'folder' });
  });
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("trailhand-dropdown", {
        style: {"flex":"1"},
        options: _unref(types),
        value: _ctx.source.type,
        label: "Source Type",
        required: true,
        placeholder: "Select a source type",
        "data-testid": "epinio_app-source_type",
        onDropdownChange: _cache[0] || (_cache[0] = (e) => _ctx.updateSource(e.detail.value))
      }, null, 40, _hoisted_3),
      _cache[9] || (_cache[9] = _createTextVNode()),
      _createElementVNode("trailhand-button", {
        variant: "alternate",
        "data-testid": "epinio_app-source_manifest",
        onButtonClick: handleFromManifestClick
      }, "\n        From Manifest\n      ", 32),
      _cache[10] || (_cache[10] = _createTextVNode()),
      _createElementVNode("input", {
        ref_key: "manifestFileInput",
        ref: manifestFileInput,
        type: "file",
        class: "hidden-file-input",
        onChange: handleManifestFileChange,
        onCancel: _cache[1] || (_cache[1] = ($event: any) => (fileDialogActive.value = false))
      }, null, 544)
    ]),
    _cache[26] || (_cache[26] = _createTextVNode()),
    (_ctx.source.type === _unref(APPLICATION_SOURCE_TYPE).ARCHIVE)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.archive.file.label')), 1),
          _cache[13] || (_cache[13] = _createTextVNode()),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("trailhand-text-input", {
              style: {"flex":"1"},
              value: _ctx.source.archive?.name || '',
              "data-testid": "epinio_app-source_archive_name",
              disabled: true,
              label: _unref(t)('epinio.applications.steps.source.archive.file.inputLabel'),
              required: true
            }, null, 8, _hoisted_6),
            _cache[11] || (_cache[11] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "alternate",
              "data-testid": "epinio_app-source_archive_file",
              onButtonClick: handleArchiveFileClick
            }, "\n            Select File\n          ", 32),
            _cache[12] || (_cache[12] = _createTextVNode()),
            _createElementVNode("input", {
              ref_key: "archiveFileInput",
              ref: archiveFileInput,
              type: "file",
              class: "hidden-file-input",
              accept: ".zip, .tar, .gz, .bz2, .xz",
              onChange: handleArchiveFileChange,
              onCancel: _cache[2] || (_cache[2] = ($event: any) => (fileDialogActive.value = false))
            }, null, 544)
          ])
        ]))
      : (_ctx.source.type === _unref(APPLICATION_SOURCE_TYPE).FOLDER)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.folder.file.label')), 1),
            _cache[16] || (_cache[16] = _createTextVNode()),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("trailhand-text-input", {
                style: {"flex":"1"},
                value: _ctx.source.folder?.name || '',
                "data-testid": "epinio_app-source_folder_name",
                disabled: true,
                label: _unref(t)('epinio.applications.steps.source.folder.file.inputLabel'),
                required: true
              }, null, 8, _hoisted_9),
              _cache[14] || (_cache[14] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "alternate",
                "data-testid": "epinio_app-source_folder_file",
                onButtonClick: handleFolderFileClick
              }, "\n            Select Folder\n          ", 32),
              _cache[15] || (_cache[15] = _createTextVNode()),
              _createElementVNode("input", {
                ref_key: "folderFileInput",
                ref: folderFileInput,
                type: "file",
                webkitdirectory: "",
                class: "hidden-file-input",
                onChange: handleFolderFileChange,
                onCancel: _cache[3] || (_cache[3] = ($event: any) => (fileDialogActive.value = false))
              }, null, 544)
            ])
          ]))
        : (_ctx.source.type === _unref(APPLICATION_SOURCE_TYPE).CONTAINER_URL)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.containerUrl.url.label')), 1),
              _cache[17] || (_cache[17] = _createTextVNode()),
              _createElementVNode("trailhand-text-input", {
                style: {"width":"100%"},
                value: _ctx.source.containerUrl?.url || '',
                "data-testid": "epinio_app-source_container",
                label: _unref(t)('epinio.applications.steps.source.containerUrl.url.inputLabel'),
                required: true,
                onTextInputChange: _cache[4] || (_cache[4] = (e) => { _ctx.updateSource(_ctx.source.type, { url: e.detail.value }); })
              }, null, 40, _hoisted_11)
            ]))
          : (_ctx.source.type === _unref(APPLICATION_SOURCE_TYPE).GIT_URL)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                (!gitConfigsForbidden.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                      _cache[18] || (_cache[18] = _createElementVNode("h3", null, "Git Config", -1)),
                      _cache[19] || (_cache[19] = _createTextVNode()),
                      _createElementVNode("trailhand-dropdown", {
                        style: {"width":"100%"},
                        value: _ctx.source.gitUrl?.gitConfig,
                        options: (_unref(gitConfigs)?.items || []).map((c) => ({ value: c.meta.name, label: c.meta.name })),
                        label: "Git Config",
                        disabled: isEdit.value,
                        filterable: "",
                        loading: _unref(isLoadingGitConfigs),
                        onDropdownChange: _cache[5] || (_cache[5] = (e) => { _ctx.updateSource(_ctx.source.type, { gitConfig: e.detail.value }); }),
                        onDropdownFilter: _cache[6] || (_cache[6] = (e) => { _unref(onGitConfigFilter)(e.detail.filter); })
                      }, null, 40, _hoisted_13),
                      _cache[20] || (_cache[20] = _createTextVNode()),
                      (_unref(isErrorGitConfigs))
                        ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(_unref(t)(`epinio.gitConfigs.errors.fetchAll`)), 1))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true),
                _cache[24] || (_cache[24] = _createTextVNode()),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.gitUrl.url.label')), 1),
                  _cache[21] || (_cache[21] = _createTextVNode()),
                  _createElementVNode("trailhand-text-input", {
                    style: {"width":"100%"},
                    value: _ctx.source.gitUrl?.url,
                    "data-testid": "epinio_app-source_git-url",
                    label: _unref(t)('epinio.applications.steps.source.gitUrl.url.inputLabel'),
                    placeholder: 'https://github.com/{user or org}/{repository}',
                    required: true,
                    onTextInputChange: _cache[7] || (_cache[7] = (e) => { _ctx.updateSource(_ctx.source.type, { url: e.detail.value, branch: '' }); })
                  }, null, 40, _hoisted_16),
                  _cache[22] || (_cache[22] = _createTextVNode()),
                  (_ctx.source.gitUrl?.url && !validGitUrl.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_17, _toDisplayString(_unref(t)('epinio.applications.steps.source.gitUrl.error.label')), 1))
                    : _createCommentVNode("", true)
                ]),
                _cache[25] || (_cache[25] = _createTextVNode()),
                _createElementVNode("div", _hoisted_18, [
                  _createElementVNode("h3", null, _toDisplayString(_unref(t)('epinio.applications.steps.source.gitUrl.branch.label')), 1),
                  _cache[23] || (_cache[23] = _createTextVNode()),
                  _createElementVNode("trailhand-text-input", {
                    style: {"width":"100%"},
                    value: _ctx.source.gitUrl?.branch,
                    "data-testid": "epinio_app-source_git-branch",
                    label: _unref(t)('epinio.applications.steps.source.gitUrl.branch.inputLabel'),
                    required: true,
                    disabled: !validGitUrl.value,
                    onTextInputChange: _cache[8] || (_cache[8] = (e) => { _ctx.updateSource(_ctx.source.type, { branch: e.detail.value }); })
                  }, null, 40, _hoisted_19)
                ])
              ], 64))
            : (_openBlock(), _createBlock(GitPicker, {
                key: 4,
                gitSource: gitPickerSource.value,
                type: gitPickerType.value,
                gitConfigs: _unref(gitConfigs)?.items || [],
                gitConfigsForbidden: gitConfigsForbidden.value,
                onGitConfigFilter: _unref(onGitConfigFilter),
                isLoadingGitConfigs: _unref(isLoadingGitConfigs),
                isErrorGitConfigs: _unref(isErrorGitConfigs),
                updateSource: _ctx.updateSource
              }, null, 8, ["gitSource", "type", "gitConfigs", "gitConfigsForbidden", "onGitConfigFilter", "isLoadingGitConfigs", "isErrorGitConfigs", "updateSource"]))
  ]))
}
}

})